<template>
  <base-input class="base-date-range-picker mb-0">
    <el-date-picker
      :value="value"
      type="daterange"
      range-separator="—"
      :start-placeholder="startPlaceholder"
      :end-placeholder="endPlaceholder"
      value-format="yyyy-MM-dd"
      :format="resolvedFormat"
      :clearable="false"
      :editable="false"
      :size="pickerSize"
      :picker-options="pickerOptions"
      :disabled="disabled"
      @input="onInput"
      @visible-change="onVisibleChange"
    />
  </base-input>
</template>

<script>
import { pickerDateFormat } from '@/plugins/dates'

function readGroup(){
  try {
    return JSON.parse(localStorage.getItem('group')) || {}
  } catch(e){
    return {}
  }
}

export default {
  name: 'base-date-range-picker',
  props: {
    value: {
      type: Array,
      default: () => [],
      validator: v => Array.isArray(v) && (v.length === 0 || v.length === 2)
    },
    maxDays: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    format: { type: String, default: '' },
    size: {
      type: String,
      default: 'default',
      validator: v => ['default', 'small', 'sm'].includes(v)
    },
    startPlaceholder: { type: String, default: 'Start' },
    endPlaceholder: { type: String, default: 'End' }
  },
  data(){
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      pickedStart: null
    }
  },
  mounted(){
    this._onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy(){
    if(this._onResize) window.removeEventListener('resize', this._onResize)
  },
  computed: {
    isMobileViewport(){
      return this.windowWidth < 576
    },
    pickerSize(){
      return (this.size === 'small' || this.size === 'sm' || this.isMobileViewport) ? 'small' : ''
    },
    group(){
      return readGroup()
    },
    resolvedFormat(){
      if(this.format) return this.format
      const base = pickerDateFormat(this.group.date_format)
      const isCompact = this.size === 'small' || this.size === 'sm' || this.isMobileViewport
      return (this.group.show_day_of_week && !isCompact) ? `ddd, ${base}` : base
    },
    pickerOptions(){
      const self = this
      return {
        firstDayOfWeek: this.group.first_day_of_week || 7,
        // Track the first-clicked date so we can constrain the second click
        onPick({ minDate, maxDate }){
          // When user clicks first date, minDate is set and maxDate is null
          // When user clicks second date, both are set
          self.pickedStart = (minDate && !maxDate) ? minDate : null
        },
        disabledDate(time){
          if(!self.maxDays || self.maxDays <= 0) return false
          if(!self.pickedStart) return false
          const startMs = self.pickedStart.getTime()
          const dayMs = 86400000
          const min = startMs - (self.maxDays - 1) * dayMs
          const max = startMs + (self.maxDays - 1) * dayMs
          return time.getTime() < min || time.getTime() > max
        }
      }
    }
  },
  methods: {
    onInput(val){
      this.pickedStart = null
      const next = Array.isArray(val) ? val : []
      this.$emit('input', next)
      this.$emit('change', next)
    },
    onVisibleChange(open){
      // Reset pickedStart when the popup closes — prevents stale disabledDate
      // anchor on the next open if user closed without picking an end date.
      if (!open) this.pickedStart = null
    }
  }
}
</script>

<style lang="scss" scoped>
.base-date-range-picker {
  flex: 1 1 auto;
  min-width: 240px;

  ::v-deep .el-range-editor.el-input__inner {
    width: 100%;
    height: 40px;
    padding: 0 10px;
    background: transparent;
    border-color: rgba(225, 78, 202, 0.4);
    border-radius: 0.4285rem;
  }

  ::v-deep .el-range-editor.el-input__inner.is-active,
  ::v-deep .el-range-editor.el-input__inner:focus-within {
    border-color: #e14eca;
  }

  ::v-deep .el-range-input {
    font-size: 13px;
    color: #fff;
  }

  ::v-deep .el-range-separator {
    line-height: 40px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
  }

  ::v-deep .el-range__icon {
    line-height: 40px;
    color: rgba(255, 255, 255, 0.6);
  }
}

@media (max-width: 575.98px) {
  .base-date-range-picker {
    min-width: 200px;

    ::v-deep .el-range-editor.el-input__inner {
      height: 32px;
      padding: 0 8px;
    }

    ::v-deep .el-range-input {
      font-size: 12px;
    }

    ::v-deep .el-range-separator {
      line-height: 32px;
      font-size: 12px;
    }

    ::v-deep .el-range__icon {
      line-height: 32px;
    }
  }
}
</style>

<style lang="scss">
// Light-mode overrides — unscoped so the .white-content parent selector reaches in
.white-content .base-date-range-picker {
  ::v-deep .el-range-editor.el-input__inner {
    background: #fff;
    border-color: rgba(225, 78, 202, 0.4);
  }

  ::v-deep .el-range-input,
  ::v-deep .el-range-separator {
    color: #344675;
  }

  ::v-deep .el-range__icon {
    color: rgba(0, 0, 0, 0.4);
  }
}

// Daterange popup — collapse to single panel on mobile so it fits the viewport.
// Default popup is ~600px wide (two calendars side-by-side); shrink to one
// calendar (~300px) anywhere the viewport can't comfortably fit two.
// The popper renders body-level so the override must be unscoped/global.
@media (max-width: 640px) {
  .el-picker-panel.el-date-range-picker {
    width: 300px !important;
    min-width: 300px !important;
    max-width: calc(100vw - 16px) !important;

    .el-picker-panel__body {
      min-width: 0 !important;
    }

    .el-date-range-picker__content.is-right {
      display: none;
    }
    .el-date-range-picker__content.is-left {
      width: 100% !important;
      float: none;
      border-right: none;
      padding: 0 12px;
    }
  }
}
</style>
